import { UseInfiniteQueryOptions, useQueryClient } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import { TPlantControlSchedulerRevision } from '@/features/plants/schemas/schedule-plant-revision.schema';

interface ScheduleRevisionParams {
  plantId?: string;
  scheduleId?: string;
  limit?: number;
  enabled?: boolean;
}

interface PageParam {
  nextCursor?: string;
  currentPage: number;
}

export interface GetSchedulePlantsResponse {
  length: number;
  body: TPlantControlSchedulerRevision[];
  nextPage?: number | null;
  nextCursor?: string | null;
  currentPage?: number;
}

async function fetchScheduleRevision(
  pageParam: PageParam,
  { plantId, scheduleId, limit = 10 }: ScheduleRevisionParams
): Promise<GetSchedulePlantsResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    currentPage: String(pageParam.currentPage),
    ...(pageParam.nextCursor && { nextCursor: pageParam.nextCursor }),
  });

  try {
    const { data } = await client.get<GetSchedulePlantsResponse>(
      `/plants/${plantId}/schedules/${scheduleId}/revisions?${params}`
    );
    return data;
  } catch (error) {
    const message = axios.isAxiosError(error)
      ? error.response?.data?.message
      : error instanceof Error
        ? error.message
        : 'An unknown error occurred';
    throw new Error(message);
  }
}

export const useGetRevisionsSchedulePlantQuery = (
  { plantId = '', scheduleId = '', enabled = true, limit = 10 }: ScheduleRevisionParams = {
    plantId: undefined,
    scheduleId: undefined,
    enabled: true,
    limit: 10,
  },
  config?: UseInfiniteQueryOptions<
    GetSchedulePlantsResponse,
    Error,
    TPlantControlSchedulerRevision[],
    string[],
    PageParam
  >
) => {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    ...config,
    queryKey: ['plants', plantId, 'schedules', scheduleId, 'revisions'],
    queryFn: ({ pageParam }) =>
      fetchScheduleRevision(pageParam, { plantId, scheduleId, limit }),
    initialPageParam: { nextCursor: undefined, currentPage: 0 },
    getNextPageParam: (lastPage) =>
      lastPage?.nextPage && lastPage?.nextCursor
        ? { nextCursor: lastPage.nextCursor, currentPage: lastPage.nextPage }
        : undefined,
    select: (data) => ({
      ...data,
      pages: data.pages.flatMap((page) => page.body),
      isLastPage: !Boolean(data.pages.at(-1)?.nextCursor),
    }),
    enabled: enabled && Boolean(plantId) && Boolean(scheduleId),
    staleTime: 60_000,
  });

  const prefetchScheduleRevision = (newPlantId?: string, newScheduleId?: string) => {
    if (!newPlantId || !newScheduleId) return;

    queryClient.prefetchInfiniteQuery({
      queryKey: ['plants', newPlantId, 'schedules', newScheduleId, 'revisions'],
      queryFn: ({ pageParam }) =>
        fetchScheduleRevision(pageParam, {
          plantId: newPlantId,
          scheduleId: newScheduleId,
          limit,
        }),
      initialPageParam: { nextCursor: undefined, currentPage: 0 },
      staleTime: 60_000,
    });
  };

  return {
    ...query,
    prefetchScheduleRevision,
  };
};
